// src/pages/dashboard/components/PackingActivity.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Box, Truck, ChevronRight, ClipboardList } from "lucide-react";
import { useLocalization } from "../../../contexts/LocalizationContext";
import { formatCurrency } from "../../../utils/currencyUtil";
import { usePermissions } from "../../../hooks/usePermissions";
import { getDashboardSummary } from "../../../services/api/packingBatch";
import { getUserWarehouses } from "../../../services/api/packingBatch";
import styles from "./PackingActivity.module.css";

const PackingActivity = ({ currency }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  useEffect(() => {
    if (hasPermission("view_all_packing_batches")) {
      fetchWarehouses();
    }
  }, []);

  useEffect(() => {
    if (hasPermission("view_all_packing_batches")) {
      fetchSummary();
    }
  }, [selectedWarehouse]);

  const fetchWarehouses = async () => {
    try {
      const response = await getUserWarehouses();
      setWarehouses(response.warehouses || []);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedWarehouse) {
        params.warehouseId = selectedWarehouse;
      }
      const response = await getDashboardSummary(params);
      setData(response);
    } catch (err) {
      console.error("Error fetching packing activity:", err);
      setError(err.message || t("dashboard.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  if (!hasPermission("view_all_packing_batches")) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t("dashboard.packingActivity")}</h3>
        </div>
        <div className={styles.skeleton}>
          <div className={styles.skeletonBar}></div>
          <div className={styles.skeletonBar}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t("dashboard.packingActivity")}</h3>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchSummary} className={styles.retryButton}>
            {t("dashboard.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { packingBatches, inPackingOrders, readyToPack, dispatchedToday } = data;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t("dashboard.packingActivity")}</h3>
        <Link to="/packing-batches" className={styles.viewAllLink}>
          {t("dashboard.viewAll")}
          <ChevronRight size={16} />
        </Link>
      </div>

      {warehouses.length > 1 && (
        <div className={styles.filters}>
          <select
            className={styles.warehouseSelect}
            value={selectedWarehouse || ""}
            onChange={(e) =>
              setSelectedWarehouse(
                e.target.value ? parseInt(e.target.value) : null
              )
            }
          >
            <option value="">{t("dashboard.allWarehouses")}</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.metricsGrid}>

          <div className={styles.metricCard}>
            <div
              className={styles.metricIcon}
              style={{ backgroundColor: "#fee2e2", color: "#ef4444" }}
            >
              <ClipboardList size={20} />
            </div>
            <div className={styles.metricInfo}>
              <div className={styles.metricValue}>
                {formatNumber(readyToPack.count)}
              </div>
              <div className={styles.metricLabel}>
                {t("dashboard.readyToPack")}
              </div>
              <div className={styles.metricSubValue}>
                {formatCurrency(readyToPack.totalValue, currency)}
              </div>
            </div>
          </div>
          
          <div className={styles.metricCard}>
            <div
              className={styles.metricIcon}
              style={{ backgroundColor: "#e0e7ff", color: "#7F5CFF" }}
            >
              <Box size={20} />
            </div>
            <div className={styles.metricInfo}>
              <div className={styles.metricValue}>
                {formatNumber(packingBatches.todayCount)}
              </div>
              <div className={styles.metricLabel}>
                {t("dashboard.todayBatches")}
              </div>
              <div className={styles.metricSubValue}>
                {formatNumber(packingBatches.totalOrders)}{" "}
                {t("dashboard.orders")}
              </div>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div
              className={styles.metricIcon}
              style={{ backgroundColor: "#fef3c7", color: "#f59e0b" }}
            >
              <Package size={20} />
            </div>
            <div className={styles.metricInfo}>
              <div className={styles.metricValue}>
                {formatNumber(inPackingOrders.count)}
              </div>
              <div className={styles.metricLabel}>
                {t("dashboard.inPacking")}
              </div>
              <div className={styles.metricSubValue}>
                {formatCurrency(inPackingOrders.totalValue, currency)}
              </div>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div
              className={styles.metricIcon}
              style={{ backgroundColor: "#d1fae5", color: "#10b981" }}
            >
              <Truck size={20} />
            </div>
            <div className={styles.metricInfo}>
              <div className={styles.metricValue}>
                {formatNumber(dispatchedToday.count)}
              </div>
              <div className={styles.metricLabel}>
                {t("dashboard.dispatchedToday")}
              </div>
              <div className={styles.metricSubValue}>
                {formatCurrency(dispatchedToday.totalValue, currency)}
              </div>
            </div>
          </div>
        </div>

        {dispatchedToday.courierBreakdown &&
          dispatchedToday.courierBreakdown.length > 0 && (
            <div className={styles.courierSection}>
              <h4 className={styles.sectionTitle}>
                {t("dashboard.courierBreakdown")}
              </h4>
              <div className={styles.courierList}>
                {dispatchedToday.courierBreakdown.map((courier, index) => (
                  <div key={index} className={styles.courierItem}>
                    <div className={styles.courierHeader}>
                      <div className={styles.courierInfo}>
                        <div
                          className={styles.courierDot}
                          style={{
                            backgroundColor:
                              courier.courierColorCode || "#94a3b8",
                          }}
                        ></div>
                        <div className={styles.courierDetails}>
                          <span className={styles.courierName}>
                            {courier.accountName}
                          </span>
                          <span className={styles.courierCompany}>
                            {courier.courierCompanyName}
                          </span>
                        </div>
                      </div>
                      <span className={styles.courierCount}>
                        {formatNumber(courier.orderCount)}
                      </span>
                    </div>
                    <div className={styles.courierValue}>
                      {formatCurrency(courier.totalValue, currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default PackingActivity;
